import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, type Observable, of } from 'rxjs';

import {
  type CursorPageRequest,
  type CursorPaginatedResponse,
} from '../../models/CursorPagination';
import { type DancerFilters, DancerModel } from '../../models/Dancer';
import { type LeaderboardItem } from '../../models/Leaderboard';
import { type ParticipantExtendedDto } from '../../models/Participant';
import { type PoolModel } from '../../models/Pool';
import { type PoolFilters } from '../../models/PoolFilters';
import { type PoolSort } from '../../models/PoolSort';
import { PoolStatus } from '../../models/Status';
import { ContestRole, SystemRole } from '../../models/User';

import { AuthService } from './auth.service';
import { ContestService } from './contest.service';
import { ContextService } from './context.service';
import { PoolApiService } from './pool.api.service';

@Injectable({
  providedIn: 'root',
})
export class PoolService {
  // =============================================================================
  // Private Properties
  // =============================================================================

  private readonly poolApiService = inject(PoolApiService);
  private readonly contestService = inject(ContestService);
  private readonly authService = inject(AuthService);
  private readonly contextService = inject(ContextService);

  // =============================================================================
  // Public Methods
  // =============================================================================

  getPoolList(options?: { filters?: PoolFilters; sort?: PoolSort }): Observable<PoolModel[]> {
    return this.poolApiService.getAll({
      ...options,
      filters: options?.filters as Record<string, string | number | boolean | null>,
    });
  }

  /** Cursor-paginated root pool list — for the unscoped (all-contests) admin view. */
  getPoolPage(options?: {
    filters?: PoolFilters;
    sort?: PoolSort;
    pagination?: CursorPageRequest;
  }): Observable<CursorPaginatedResponse<PoolModel>> {
    return this.poolApiService.getPage({
      ...options,
      filters: options?.filters as Record<string, string | number | boolean | null>,
    });
  }

  getPoolById(id: string): Observable<PoolModel> {
    return this.poolApiService.getById(id);
  }

  getPoolByName(name: string): Observable<PoolModel> {
    return this.poolApiService.getAll({ filters: { name } }).pipe(
      map((pools) => {
        if (!pools || pools.length === 0) {
          throw new Error('Pool not found');
        }
        return pools[0];
      }),
    );
  }

  getPoolByIdOrName(idOrName: string): Observable<PoolModel> {
    // Try UUID format check or just try ID first
    return this.getPoolById(idOrName).pipe(catchError(() => this.getPoolByName(idOrName)));
  }

  createPool(pool: Omit<PoolModel, 'id'>): Observable<PoolModel> {
    return this.poolApiService.post(pool);
  }

  updatePool(id: string, pool: Partial<PoolModel>): Observable<PoolModel> {
    return this.poolApiService.put(id, pool);
  }

  removePool(id: string): Observable<void> {
    return this.poolApiService.delete(id).pipe(map(() => void 0));
  }

  deletePoolList(pools: PoolModel[]): Observable<void[]> {
    if (pools.length === 0) return of([]);
    return forkJoin(pools.map((g) => this.removePool(g.id)));
  }

  updatePoolStatus(
    id: string,
    status: PoolStatus,
    skipScoreCheck = false,
    skipStatusCheck = false,
  ): Observable<PoolModel> {
    return this.poolApiService.updateStatus(id, status, skipScoreCheck, skipStatusCheck);
  }

  updatePoolListStatus(
    pools: PoolModel[],
    status: PoolStatus,
    skipScoreCheck = false,
    skipStatusCheck = false,
  ): Observable<PoolModel[]> {
    const updateObservables = pools.map((pool) =>
      this.updatePoolStatus(pool.id, status, skipScoreCheck, skipStatusCheck),
    );
    return forkJoin(updateObservables);
  }

  getDancerListOfPool(
    poolId: string,
    options?: {
      filters?: DancerFilters;
    },
  ): Observable<DancerModel[]> {
    const filters = options?.filters;
    return this.poolApiService
      .getParticipantsOfPool(poolId, {
        filters: {
          contestRole: filters?.contestRole,
          dancerRole: filters?.dancerRole,
        },
      })
      .pipe(
        map((participants: ParticipantExtendedDto[]) => {
          const currentUserContestRole = this.contextService.activeParticipant()?.contestRole;
          const currentUserSystemRole = this.contextService.currentUser()?.systemRole;

          const canSeeSensitiveInfo =
            currentUserSystemRole === SystemRole.SuperAdmin ||
            currentUserSystemRole === SystemRole.Admin ||
            currentUserContestRole === ContestRole.Organizer;

          return participants.map((p) => {
            const dancer = new DancerModel();
            // Map properties from ParticipantExtendedDto to DancerModel
            Object.assign(dancer, p);
            // Map flattened user properties
            if (p.user) {
              dancer.username = p.user.username;
              dancer.systemRole = p.user.systemRole;
              dancer.isShadow = p.user.isShadow;

              if (canSeeSensitiveInfo) {
                dancer.firstName = p.user.firstName;
                dancer.lastName = p.user.lastName;
                dancer.email = p.user.email;
                dancer.accountStatus = p.user.status;
              } else {
                // Hide sensitive info
                dancer.firstName = 'Hidden';
                dancer.lastName = 'Hidden';
                dancer.email = 'hidden@example.com';
                dancer.accountStatus = p.user.status; // Keep status as it's often visible (e.g. ACTIVE)
              }
            }
            return dancer;
          });
        }),
      );
  }

  getPoolLeaderboard(poolId: string): Observable<LeaderboardItem[]> {
    return this.poolApiService.getPoolLeaderboard(poolId);
  }

  addParticipantsToPool(
    poolId: string,
    participantIds: string[],
    skipStatusCheck = false,
  ): Observable<unknown> {
    return this.poolApiService.addParticipantsToPool(poolId, participantIds, skipStatusCheck);
  }

  removeParticipantsFromPool(poolId: string, participantIds: string[]): Observable<unknown> {
    return this.poolApiService.removeParticipantsFromPool(poolId, participantIds);
  }

  /** Returns the theme color associated with a pool status. */
  getStatusColor(status: PoolStatus): 'primary' | 'accent' | 'warn' {
    switch (status) {
      case PoolStatus.UPCOMING:
        return 'primary';
      case PoolStatus.ACTIVE:
        return 'accent';
      case PoolStatus.FINISHED:
        return 'warn';
      default:
        return 'primary';
    }
  }

  getAvailableDancersForPool(contestId: string, _poolId: string): Observable<DancerModel[]> {
    // Get all dancers in contest
    return this.contestService
      .getParticipantList(contestId, {
        filters: {
          contestRole: ContestRole.Dancer,
        },
      })
      .pipe(
        map((participants) =>
          participants.map((p) => {
            const dancer = new DancerModel();
            Object.assign(dancer, p);
            return dancer;
          }),
        ),
      );
  }
}
